import {
  CheckCircleFilled,
  SafetyCertificateOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { Button, message } from 'antd';
import React, { useState } from 'react';
import {
  InsuranceAAData,
  RcData,
  daysBetween,
  formatDate,
  formatInr,
} from './mockData';
import styles from './styles.module.css';

type Props = {
  rc: RcData;
  insuranceAA: InsuranceAAData;
  onLinkAA: () => void;
  onBuy: () => void;
  onRenew: () => void;
};

const titleCase = (s: string) =>
  s
    .toLowerCase()
    .split(' ')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');

const InsuranceSection: React.FC<Props> = ({
  rc,
  insuranceAA,
  onLinkAA,
  onBuy,
  onRenew,
}) => {
  const [linking, setLinking] = useState(false);
  const days = daysBetween(rc.insurance_data.expiry_date);
  const needsBuy = days <= 30; // expired or expiring within 30 days

  // ------------------- STATE C: Expired / expiring → Buy --------------------
  if (needsBuy) {
    return (
      <div className={`${styles.insSection} ${styles.insBuy}`}>
        <div className={styles.insHeadRow}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div
              className={styles.insHeadIcon}
              style={{ background: 'rgba(255,255,255,0.2)' }}
            >
              <WarningFilled />
            </div>
            <div>
              <div className={styles.insLabel}>Insurance</div>
              <div className={styles.insTitle}>
                {days < 0 ? 'Policy expired' : `Expires in ${days} days`}
              </div>
              <div className={styles.insSub}>
                {titleCase(rc.insurance_data.company)} ·{' '}
                {formatDate(rc.insurance_data.expiry_date)}
              </div>
            </div>
          </div>
          <span className={`${styles.insBadge} ${styles.insBadgeWarn}`}>
            {days < 0 ? 'Lapsed' : 'Urgent'}
          </span>
        </div>

        <div className={styles.insSub} style={{ marginTop: 4 }}>
          Driving without active insurance is an offence. Compare quotes from
          4+ IRDAI-licensed insurers — premiums starting ₹4,280/year with
          zero-dep & engine protect.
        </div>

        <div className={styles.insCtaRow}>
          <Button className={styles.insCtaPrimary} onClick={onBuy}>
            Compare &amp; buy
          </Button>
          <Button className={styles.insCtaSecondary} onClick={onRenew}>
            Renew with {titleCase(rc.insurance_data.company).split(' ')[0]}
          </Button>
        </div>
      </div>
    );
  }

  // ------------------- STATE B: Active + AA-linked → Rich details ------------
  if (insuranceAA.linked) {
    return (
      <div className={`${styles.insSection} ${styles.insLinked}`}>
        <div className={styles.insHeadRow}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div
              className={styles.insHeadIcon}
              style={{ background: '#e0e7ff', color: '#3730a3' }}
            >
              <SafetyCertificateOutlined />
            </div>
            <div>
              <div className={styles.insLabel}>Insurance · linked via AA</div>
              <div className={styles.insTitle} style={{ color: '#111' }}>
                {titleCase(rc.insurance_data.company)}
              </div>
              <div className={styles.insSub} style={{ color: '#6b7280' }}>
                Policy {rc.insurance_data.policy_number} · valid till{' '}
                {formatDate(rc.insurance_data.expiry_date)}
              </div>
            </div>
          </div>
          <span className={`${styles.insBadge} ${styles.insBadgeActive}`}>
            Active · {days} d left
          </span>
        </div>

        <div className={styles.insGrid}>
          <div className={styles.insGridBox}>
            <div className={styles.insGridLabel}>Annual premium</div>
            <div className={styles.insGridValue}>
              {formatInr(insuranceAA.premium ?? 0)}
            </div>
          </div>
          <div className={styles.insGridBox}>
            <div className={styles.insGridLabel}>IDV</div>
            <div className={styles.insGridValue}>
              {formatInr(insuranceAA.idv ?? 0)}
            </div>
          </div>
          <div className={styles.insGridBox}>
            <div className={styles.insGridLabel}>NCB</div>
            <div className={styles.insGridValue}>
              {insuranceAA.ncb ?? 0}%
            </div>
          </div>
        </div>

        {insuranceAA.addOns && insuranceAA.addOns.length > 0 && (
          <div>
            <div
              className={styles.insGridLabel}
              style={{ marginBottom: 6, color: '#6b7280' }}
            >
              Add-ons
            </div>
            <div className={styles.insAddonRow}>
              {insuranceAA.addOns.map((a) => (
                <span key={a} className={styles.insAddonChip}>
                  {a}
                </span>
              ))}
            </div>
          </div>
        )}

        {insuranceAA.lastClaim && (
          <div>
            <div
              className={styles.insGridLabel}
              style={{ marginBottom: 6, color: '#6b7280' }}
            >
              Last claim
            </div>
            <div className={styles.insClaimRow}>
              <div className={styles.insClaimLeft}>
                <span className={styles.insClaimAmount}>
                  {formatInr(insuranceAA.lastClaim.amount)}
                </span>
                <span className={styles.insClaimMeta}>
                  {formatDate(insuranceAA.lastClaim.date)} ·{' '}
                  {insuranceAA.claimSettlementRatio}% insurer settlement ratio
                </span>
              </div>
              <span className={styles.insClaimStatus}>
                {insuranceAA.lastClaim.status}
              </span>
            </div>
          </div>
        )}

        <div className={styles.insCtaRow}>
          <Button
            className={styles.insCtaPrimaryDark}
            onClick={() => message.success('Claim flow opening…')}
          >
            File a claim
          </Button>
          <Button
            className={styles.insCtaSecondaryDark}
            onClick={onRenew}
          >
            Renew policy
          </Button>
        </div>
      </div>
    );
  }

  // ------------------- STATE A: Active + not linked → Link via AA ------------
  return (
    <div className={`${styles.insSection} ${styles.insUnlinked}`}>
      <div className={styles.insHeadRow}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div
            className={styles.insHeadIcon}
            style={{ background: 'rgba(255,255,255,0.2)' }}
          >
            <SafetyCertificateOutlined />
          </div>
          <div>
            <div className={styles.insLabel}>Insurance</div>
            <div className={styles.insTitle}>
              {titleCase(rc.insurance_data.company)}
            </div>
            <div className={styles.insSub}>
              Policy {rc.insurance_data.policy_number} · valid till{' '}
              {formatDate(rc.insurance_data.expiry_date)}
            </div>
          </div>
        </div>
        <span className={`${styles.insBadge} ${styles.insBadgeWarn}`}>
          {days} days left
        </span>
      </div>

      <div className={styles.insSub} style={{ marginTop: 4 }}>
        <CheckCircleFilled style={{ color: '#86efac' }} /> You have active
        coverage. Link via Account Aggregator to see premium paid, IDV, NCB,
        add-ons and claim history.
      </div>

      <div className={styles.insCtaRow}>
        <Button
          className={styles.insCtaPrimary}
          loading={linking}
          onClick={() => {
            setLinking(true);
            setTimeout(() => {
              onLinkAA();
              setLinking(false);
              message.success('Insurance linked via Account Aggregator');
            }, 1200);
          }}
        >
          Link via Account Aggregator
        </Button>
        <Button className={styles.insCtaSecondary} onClick={onRenew}>
          Pay / renew
        </Button>
      </div>
    </div>
  );
};

export default InsuranceSection;
